import { Controller } from "@hotwired/stimulus"
import { play, setEnabled, setTheme, unlock } from "cuelume"

// Beta scanner at /qr_scanners/beta. Posts to the same endpoint as qr_scanner_controller.js,
// but gives each result a sound, an ink stamp and a rolling count so volunteers don't have to read it.
const SOUND_KEY = "scanner-sound"
const MILESTONE_EVERY = 50
const RESUME_AFTER_MS = 1300
const HOLD_MS = 1200

const OUTCOMES = {
  valid: { tone: "ok", icon: "✓", title: "Stamped", cue: "success", emphasis: "strong", recent: "Valid" },
  duplicate: { tone: "warn", icon: "!", title: "Already stamped", cue: "warning", recent: "Repeat" },
  invalid: { tone: "muted", icon: "✕", title: "Not a passport", cue: "error", recent: "Invalid" },
  failed: { tone: "bad", icon: "⚠", title: "Not saved · tap to retry", cue: "error", emphasis: "strong" }
}

export default class extends Controller {
  static targets = [
    "video", "view", "scanline", "pill", "pillText", "gate", "stamp", "stampIcon", "stampTitle", "stampDetail",
    "burst", "milestone", "count", "countLabel", "recent", "recentEmpty", "soundToggle", "soundIcon",
    "cameraOption", "powerButton", "hold", "logoutForm", "announcer"
  ]
  static values = { count: Number }

  connect() {
    this.stream = null
    this.scanning = false
    this.awaitingDismiss = false
    this.facingMode = "environment"
    this.reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches

    setTheme("mech")
    this.soundOn = localStorage.getItem(SOUND_KEY) !== "off"
    this.renderSound()
    this.loadQRLibrary()
  }

  disconnect() {
    this.stopCamera()
    clearTimeout(this.resumeTimer)
    cancelAnimationFrame(this.holdFrame)
  }

  // The tap is the user gesture that both the camera and Web Audio need on iOS
  async open() {
    unlock()
    this.gateTarget.hidden = true
    await this.startCamera()
    if (this.stream) this.cue("ready")
  }

  // Camera

  async startCamera() {
    this.setPill("Starting camera…", true)
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: this.facingMode }, width: { ideal: 1280 }, height: { ideal: 720 } }
      })
      this.videoTarget.srcObject = this.stream
      await this.videoTarget.play()
      this.powerButtonTarget.hidden = false
      this.powerButtonTarget.textContent = "Pause camera"
      this.resume()
    } catch (error) {
      this.stopCamera()
      this.setPill(this.cameraErrorMessage(error), false)
      this.gateTarget.hidden = false
      this.cue("attention")
    }
  }

  stopCamera() {
    this.scanning = false
    this.stream?.getTracks().forEach(track => track.stop())
    this.stream = null
    if (this.hasVideoTarget) this.videoTarget.srcObject = null
    if (this.hasScanlineTarget) this.scanlineTarget.hidden = true
  }

  togglePower() {
    if (this.stream) {
      this.stopCamera()
      this.hideStamp()
      this.setPill("Camera paused", false)
      this.powerButtonTarget.textContent = "Resume camera"
      this.cue("close")
    } else {
      this.cue("open")
      this.startCamera()
    }
  }

  async selectCamera(event) {
    const facing = event.currentTarget.dataset.facing
    if (facing === this.facingMode) return

    this.facingMode = facing
    this.cameraOptionTargets.forEach(option => option.setAttribute("aria-checked", String(option.dataset.facing === facing)))
    this.cue("select")
    if (this.stream) {
      this.stopCamera()
      await this.startCamera()
    }
  }

  cameraErrorMessage(error) {
    if (error.name === "NotAllowedError") return "Allow camera access, then tap again"
    if (error.name === "NotFoundError") return "No camera found on this device"
    return `Camera failed: ${error.message}`
  }

  loadQRLibrary() {
    if (window.jsQR || document.querySelector("script[data-jsqr]")) return

    const script = document.createElement("script")
    script.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js"
    script.dataset.jsqr = ""
    document.head.appendChild(script)
  }

  // Scanning

  resume() {
    if (!this.stream) return

    this.hideStamp()
    this.scanning = true
    this.scanlineTarget.hidden = false
    this.setPill("Scan a passport QR code", false)
    this.scanLoop()
  }

  scanLoop() {
    if (!window.jsQR) {
      setTimeout(() => this.scanning && this.scanLoop(), 100)
      return
    }

    const canvas = document.createElement("canvas")
    const context = canvas.getContext("2d", { willReadFrequently: true })
    const video = this.videoTarget

    const scan = () => {
      if (!this.scanning) return

      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        context.drawImage(video, 0, 0, canvas.width, canvas.height)
        const image = context.getImageData(0, 0, canvas.width, canvas.height)
        const code = window.jsQR(image.data, image.width, image.height, { inversionAttempts: "dontInvert" })
        if (code) return this.submit(code.data)
      }
      requestAnimationFrame(scan)
    }
    scan()
  }

  async submit(qrData) {
    this.scanning = false
    this.scanlineTarget.hidden = true
    this.setPill("Reading passport…", true)

    try {
      const response = await fetch("/qr_scanners", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken },
        body: JSON.stringify({ qr_data: qrData })
      })
      const result = await this.parseJSON(response)
      if (!result.success) throw new Error(result.message || `HTTP ${response.status}`)

      const outcome = result.status in OUTCOMES ? result.status : "valid"
      this.showOutcome(outcome, outcome === "invalid" ? qrData : result.connect_id)
      this.addRecent(outcome, result.connect_id || qrData)
      this.updateCount(result.valid_scans_count, outcome === "valid")
      this.resumeTimer = setTimeout(() => this.resume(), RESUME_AFTER_MS)
    } catch (error) {
      // The scan may not be saved, so keep the stamp up until the volunteer taps and rescans
      this.showOutcome("failed", error.message)
      this.awaitingDismiss = true
    }
  }

  dismissError() {
    if (!this.awaitingDismiss) return

    this.awaitingDismiss = false
    this.resume()
  }

  // Safari reports a non-JSON body only as "The string did not match the expected pattern"
  async parseJSON(response) {
    const body = await response.text()
    try {
      return JSON.parse(body)
    } catch {
      const html = new DOMParser().parseFromString(body, "text/html")
      const summary = html.title || html.body?.textContent.replace(/\s+/g, " ").trim().slice(0, 80)
      throw new Error(`HTTP ${response.status}: ${summary || "empty response"}`)
    }
  }

  // Feedback

  showOutcome(outcome, detail) {
    const { tone, icon, title, cue, emphasis } = OUTCOMES[outcome]

    this.pillTarget.hidden = true
    this.stampIconTarget.textContent = icon
    this.stampTitleTarget.textContent = title
    this.stampDetailTarget.textContent = (detail || "").slice(0, 40)
    this.stampTarget.dataset.tone = tone
    this.stampTarget.hidden = false
    // Restart the stamp animation even when two outcomes in a row look the same
    this.stampTarget.classList.remove("is-on")
    void this.stampTarget.offsetWidth
    this.stampTarget.classList.add("is-on")

    this.cue(cue, emphasis ? { emphasis } : {})
    this.announcerTarget.textContent = `${title}${detail ? `: ${detail}` : ""}`
  }

  hideStamp() {
    this.stampTarget.hidden = true
    this.stampTarget.classList.remove("is-on")
    this.milestoneTarget.hidden = true
    this.pillTarget.hidden = false
  }

  setPill(text, busy) {
    this.pillTarget.hidden = false
    this.pillTarget.classList.toggle("is-busy", busy)
    this.pillTextTarget.textContent = text
  }

  addRecent(outcome, code) {
    const item = document.createElement("li")
    const label = document.createElement("code")
    const tag = document.createElement("span")
    label.textContent = outcome === "invalid" ? "No connect ID" : code
    tag.textContent = OUTCOMES[outcome].recent
    tag.dataset.tone = OUTCOMES[outcome].tone
    item.append(label, tag)

    if (this.hasRecentEmptyTarget) this.recentEmptyTarget.remove()
    this.recentTarget.prepend(item)
    this.animate(item, [{ opacity: 0, transform: "translateY(-6px)" }, { opacity: 1, transform: "none" }], 250)
    while (this.recentTarget.children.length > 3) this.recentTarget.lastElementChild.remove()
  }

  // Rolls the count to its new value; another phone at the same event can move it by more than one
  updateCount(count, fromValidScan) {
    if (!this.hasCountTarget || count == null || count === this.countValue) return

    const previous = this.countValue
    this.countValue = count
    const old = this.countTarget.firstElementChild
    const next = document.createElement("span")
    next.textContent = count
    this.countTarget.append(next)
    this.countLabelTarget.textContent = `valid ${count === 1 ? "scan" : "scans"}`
    this.countTarget.classList.add("is-flashing")
    requestAnimationFrame(() => requestAnimationFrame(() => this.countTarget.classList.remove("is-flashing")))

    const ease = "cubic-bezier(.3, 1.3, .5, 1)"
    this.animate(old, [{ transform: "none", opacity: 1 }, { transform: "translateY(-100%)", opacity: 0 }], 260, ease)
    this.animate(next, [{ transform: "translateY(100%)", opacity: 0 }, { transform: "none", opacity: 1 }], 300, ease)
    setTimeout(() => old.remove(), this.reducedMotion ? 0 : 260)

    const milestone = Math.floor(count / MILESTONE_EVERY) * MILESTONE_EVERY
    if (fromValidScan && milestone > previous && milestone > 0) this.celebrate(milestone)
  }

  celebrate(milestone) {
    this.milestoneTarget.textContent = `🎉 ${milestone} passports stamped`
    this.milestoneTarget.hidden = false
    if (this.reducedMotion) return

    const colors = ["#9b1c31", "#e0a526", "#ff6b81", "#ffffff"]
    this.burstTarget.replaceChildren()
    for (let i = 0; i < 18; i++) {
      const spark = document.createElement("i")
      spark.style.background = colors[i % colors.length]
      this.burstTarget.append(spark)
      const angle = (i / 18) * Math.PI * 2
      const distance = 70 + Math.random() * 50
      spark.animate([
        { transform: "translate(0, 0) scale(1)", opacity: 1 },
        { transform: `translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px) scale(.3) rotate(${Math.random() * 360}deg)`, opacity: 0 }
      ], { duration: 700 + Math.random() * 300, easing: "cubic-bezier(.1, .8, .3, 1)", fill: "forwards" })
    }
  }

  animate(element, keyframes, duration, easing = "ease-out") {
    if (!this.reducedMotion) element.animate(keyframes, { duration, easing })
  }

  // Sound

  cue(name, options = {}) {
    if (this.soundOn) play(name, options)
  }

  toggleSound() {
    this.soundOn = !this.soundOn
    localStorage.setItem(SOUND_KEY, this.soundOn ? "on" : "off")
    this.renderSound()
    if (this.soundOn) {
      unlock()
      play("toggle", { direction: "forward" })
    }
  }

  renderSound() {
    setEnabled(this.soundOn)
    this.soundToggleTarget.setAttribute("aria-pressed", String(this.soundOn))
    this.soundIconTarget.textContent = this.soundOn ? "🔊" : "🔇"
  }

  // Hold to log out: fills while held and drains back when released early

  holdStart(event) {
    if (event.button > 0) return
    this.holding = true
    this.holdProgress ||= 0
    this.holdLast = performance.now()
    cancelAnimationFrame(this.holdFrame)
    this.holdFrame = requestAnimationFrame(time => this.holdStep(time))
  }

  holdStop() {
    this.holding = false
  }

  holdKeyStart(event) {
    if ((event.key === " " || event.key === "Enter") && !event.repeat) {
      event.preventDefault()
      this.holdStart(event)
    }
  }

  holdKeyStop(event) {
    if (event.key === " " || event.key === "Enter") this.holdStop()
  }

  holdStep(time) {
    const elapsed = time - this.holdLast
    this.holdLast = time
    const delta = this.holding ? elapsed / HOLD_MS : -elapsed / (HOLD_MS / 2.5)
    this.holdProgress = Math.min(1, Math.max(0, this.holdProgress + delta))
    this.holdTarget.style.setProperty("--progress", this.holdProgress)

    if (this.holdProgress >= 1) {
      this.holding = false
      this.cue("close", { emphasis: "strong" })
      this.stopCamera()
      this.logoutFormTarget.requestSubmit()
      return
    }
    if (this.holding || this.holdProgress > 0) this.holdFrame = requestAnimationFrame(next => this.holdStep(next))
  }

  preventDefault(event) {
    event.preventDefault()
  }

  get csrfToken() {
    return document.querySelector("[name='csrf-token']")?.content || ""
  }
}
